import { CONFIG } from '../config';
import type { Player } from './Player';

type LaserPhase = 'warning' | 'active' | 'done';

/**
 * 레이저 엔티티 - 경고 후 순간 발사
 */
export class Laser {
    public isHorizontal: boolean;
    public position: number; // y (horizontal) or x (vertical)
    public phase: LaserPhase;
    public timer: number;
    public isAlive: boolean;

    private warningDuration: number;
    private activeDuration: number;
    private width: number;
    private canvasWidth: number;
    private canvasHeight: number;

    constructor(isHorizontal: boolean, position: number, canvasWidth: number, canvasHeight: number) {
        this.isHorizontal = isHorizontal;
        this.position = position;
        this.phase = 'warning';
        this.timer = 0;
        this.isAlive = true;

        this.warningDuration = CONFIG.LASER.WARNING_DURATION;
        this.activeDuration = CONFIG.LASER.ACTIVE_DURATION;
        this.width = CONFIG.LASER.WIDTH;
        this.canvasWidth = canvasWidth;
        this.canvasHeight = canvasHeight;
    }

    update(deltaTime: number): void {
        this.timer += deltaTime;

        if (this.phase === 'warning') {
            if (this.timer >= this.warningDuration) {
                this.phase = 'active';
                this.timer = 0;
            }
        } else if (this.phase === 'active') {
            if (this.timer >= this.activeDuration) {
                this.phase = 'done';
                this.isAlive = false;
            }
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        const margin = CONFIG.PLAY_AREA.MARGIN;

        if (this.phase === 'warning') {
            // 경고선 (점선) - 흰색
            const warningProgress = this.timer / this.warningDuration;
            const alpha = 0.4 + Math.sin(warningProgress * Math.PI * 6) * 0.3;

            ctx.save();
            ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
            ctx.lineWidth = 2;
            ctx.setLineDash([10, 10]);

            ctx.beginPath();
            if (this.isHorizontal) {
                ctx.moveTo(margin, this.position);
                ctx.lineTo(this.canvasWidth - margin, this.position);
            } else {
                ctx.moveTo(this.position, margin);
                ctx.lineTo(this.position, this.canvasHeight - margin);
            }
            ctx.stroke();
            ctx.restore();

            // 경고 표시 (테두리 근처에 흰색 삼각형)
            this.renderWarningIndicator(ctx, margin, warningProgress);

        } else if (this.phase === 'active') {
            // 레이저 발사 - 단색 흰색
            const activeProgress = this.timer / this.activeDuration;
            const alpha = 1 - activeProgress * 0.5;

            ctx.save();
            ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;

            if (this.isHorizontal) {
                ctx.fillRect(margin, this.position - this.width / 2, this.canvasWidth - margin * 2, this.width);
            } else {
                ctx.fillRect(this.position - this.width / 2, margin, this.width, this.canvasHeight - margin * 2);
            }

            ctx.restore();
        }
    }

    private renderWarningIndicator(ctx: CanvasRenderingContext2D, margin: number, progress: number): void {
        const size = 7; // 절반 크기
        const pulse = Math.sin(progress * Math.PI * 6) * 0.3 + 0.7;

        ctx.save();
        ctx.fillStyle = `rgba(255, 255, 255, ${pulse * 0.8})`;

        // 세로모드: 캔버스가 세로로 긴 경우 (height > width)
        const isPortrait = this.canvasHeight > this.canvasWidth;

        if (this.isHorizontal) {
            // 수평 레이저 - 세로모드일 때 상단 테두리 바깥에 표시
            if (isPortrait) {
                // 상단 테두리 바깥에 삼각형 (아래 방향)
                ctx.beginPath();
                ctx.moveTo(this.canvasWidth / 2 - size, margin - size * 2);
                ctx.lineTo(this.canvasWidth / 2, margin - size);
                ctx.lineTo(this.canvasWidth / 2 + size, margin - size * 2);
                ctx.closePath();
                ctx.fill();
            } else {
                // 가로모드일 때 우측 테두리 바깥에 표시
                ctx.beginPath();
                ctx.moveTo(this.canvasWidth - margin + size, this.position - size);
                ctx.lineTo(this.canvasWidth - margin + size * 2, this.position);
                ctx.lineTo(this.canvasWidth - margin + size, this.position + size);
                ctx.closePath();
                ctx.fill();
            }
        } else {
            // 수직 레이저 - 세로모드일 때 상단 테두리 바깥에, 가로모드일 때 우측 테두리 바깥에
            if (isPortrait) {
                // 상단 테두리 바깥에 삼각형 (아래 방향)
                ctx.beginPath();
                ctx.moveTo(this.position - size, margin - size * 2);
                ctx.lineTo(this.position, margin - size);
                ctx.lineTo(this.position + size, margin - size * 2);
                ctx.closePath();
                ctx.fill();
            } else {
                // 가로모드일 때 우측 테두리 바깥에 표시
                ctx.beginPath();
                ctx.moveTo(this.canvasWidth - margin + size, this.position - size);
                ctx.lineTo(this.canvasWidth - margin + size * 2, this.position);
                ctx.lineTo(this.canvasWidth - margin + size, this.position + size);
                ctx.closePath();
                ctx.fill();
            }
        }

        ctx.restore();
    }

    /**
     * 플레이어와 충돌 체크 (active 상태일 때만)
     */
    checkCollision(player: Player): boolean {
        if (this.phase !== 'active') return false;

        const playerBounds = player.getBounds();
        const halfWidth = this.width / 2;

        if (this.isHorizontal) {
            // 수평 레이저: y 좌표 체크
            return Math.abs(playerBounds.y - this.position) < halfWidth + playerBounds.radius;
        } else {
            // 수직 레이저: x 좌표 체크
            return Math.abs(playerBounds.x - this.position) < halfWidth + playerBounds.radius;
        }
    }
}
